import { useCallback, useEffect, useState } from 'react';
import { type Site, errText, siteApi } from './common';

interface EnvKey { key: string; masked: string }

// Env-szerkesztő: csak a kulcsnevek és a maszkolt érték látszik; új érték beírható (felülír), kulcs törölhető. A teljes érték SOHA nem jön le.
export function EnvEditor({ site }: { site: Site }) {
  const [data, setData] = useState<{ path: string; keys: EnvKey[] } | null>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [vals, setVals] = useState<Record<string, string>>({});
  const [newKey, setNewKey] = useState('');
  const [newVal, setNewVal] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    siteApi<{ path?: string; keys?: EnvKey[] }>(site.id, '/env')
      .then((r) => { setData({ path: r.path ?? '', keys: r.keys ?? [] }); setErr(''); })
      .catch((e) => setErr(errText(e, 'Env-fájl')));
  }, [site.id]);
  useEffect(() => { load(); }, [load]);

  const put = async (body: { set?: Record<string, string>; unset?: string[] }, ok: string) => {
    setBusy(true); setErr('');
    try { await siteApi(site.id, '/env', { method: 'PUT', body: JSON.stringify(body) }); setMsg(ok); window.setTimeout(() => setMsg(''), 4000); load(); return true; }
    catch (e) { setErr(errText(e, 'Env mentése')); return false; } finally { setBusy(false); }
  };
  const KEY = /^[A-Za-z_][A-Za-z0-9_]*$/;

  return (
    <div className="a-site-env">
      <p className="a-site-note">Fájl: <code>{data?.path || site.env_path || '— (nincs megadva: az env_path mezőben állítsd be)'}</code> · a változás a következő indításkor / újraindításkor él.</p>
      {err && <div className="a-site-msg bad">{err}</div>}
      {msg && <div className="a-site-msg ok">{msg}</div>}
      {data && (
        <div className="a-site-tablewrap">
          <table className="a-site-table a-site-envt">
            <thead><tr><th>Kulcs</th><th>Jelenlegi (maszkolt)</th><th>Új érték</th><th /></tr></thead>
            <tbody>
              {data.keys.map((k) => (
                <tr key={k.key}>
                  <td className="mono">{k.key}</td>
                  <td className="mono mut">{k.masked}</td>
                  <td><input className="a-input mono" type="password" autoComplete="new-password" placeholder="új érték (felülírja)" value={vals[k.key] ?? ''} onChange={(e) => setVals({ ...vals, [k.key]: e.target.value })} /></td>
                  <td className="acts">
                    <button type="button" className="a-btn sm pri" disabled={busy || !vals[k.key]} onClick={() => void put({ set: { [k.key]: vals[k.key]! } }, `${k.key} frissítve.`).then((ok) => { if (ok) setVals((v) => ({ ...v, [k.key]: '' })); })}>Mentés</button>
                    <button type="button" className="a-site-mini danger" title="Kulcs törlése" disabled={busy} onClick={() => { if (confirm(`Törlöd a(z) ${k.key} kulcsot az env-fájlból?`)) void put({ unset: [k.key] }, `${k.key} törölve.`); }}>×</button>
                  </td>
                </tr>
              ))}
              {data.keys.length === 0 && <tr className="empty"><td colSpan={4}>Az env-fájl üres, vagy még nincs.</td></tr>}
              <tr className="new">
                <td><input className="a-input mono" placeholder="ÚJ_KULCS" value={newKey} onChange={(e) => setNewKey(e.target.value.trim())} /></td>
                <td className="mut">új kulcs</td>
                <td><input className="a-input mono" type="password" autoComplete="new-password" placeholder="érték" value={newVal} onChange={(e) => setNewVal(e.target.value)} /></td>
                <td className="acts"><button type="button" className="a-btn sm" disabled={busy || !KEY.test(newKey) || !newVal} onClick={() => void put({ set: { [newKey]: newVal } }, `${newKey} hozzáadva.`).then((ok) => { if (ok) { setNewKey(''); setNewVal(''); } })}>+ Hozzáadás</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
